import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { buildJobPath } from '@/utils/jobSlug';
import { siteEvents } from '@/lib/siteEvents';
import { JobAlertSignup, type AlertJob } from '@/components/JobAlertSignup';
import './closed-job-notice.css';

/**
 * "This posting closed", shown at the top of a closed job page, with open
 * roles of the same kind: same metro first, then remote, then anywhere in the
 * same country (similar_open_jobs RPC, about 20 ms).
 *
 * Why: one in ten human job landings in the week to 2 October was on a closed
 * posting, and the page rendered it exactly like a live one, Apply button and
 * all. Answer engines keep citing a job for days after it closes; ChatGPT
 * visitors, the readers most likely to apply, landed on closed jobs too.
 * Crawlers never see this page: the worker answers them with a 410.
 */

interface SimilarJob {
  id: string;
  title: string;
  company: string | null;
  location: string | null;
  is_remote: boolean | null;
  salary: string | null;
  post_date: string | null;
}
interface Similar { role: string | null; role_key: string | null; closed_at: string | null; jobs: SimilarJob[] }

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export function ClosedJobNotice({ jobId, title, closedAt, alertJob }: {
  jobId: string; title: string; closedAt?: string | null; alertJob?: AlertJob;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['similar_open_jobs', jobId],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as unknown as (fn: string, a: object) => Promise<{ data: unknown; error: unknown }>)(
        'similar_open_jobs', { p_job_id: jobId, p_limit: 5 });
      if (error) throw error;
      return data as Similar | null;
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 10,
  });

  const jobs = data?.jobs ?? [];
  const role = data?.role ?? null;

  React.useEffect(() => {
    if (!isLoading) siteEvents.act('closed_job_view', { similar: jobs.length });
    // One impression per job, once the suggestions have loaded (or failed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isLoading]);

  const when = closedAt ?? data?.closed_at ?? null;
  const search = `/?q=${encodeURIComponent(role ?? title)}`;

  return (
    <section className="jd-closed" aria-labelledby="jd-closed-title">
      <span className="jd-closed__eyebrow">No longer on the board</span>
      <h2 className="jd-closed__title" id="jd-closed-title">
        This posting closed{when ? ` on ${longDate(when)}` : ''}
      </h2>
      <p className="jd-closed__lede">
        The employer may have stopped taking applications.
        {jobs.length > 0 ? ` These ${role ?? 'similar'} roles are open now.` : ''}
      </p>

      {isLoading && <div className="jd-closed__loading" aria-hidden="true" />}

      {jobs.length > 0 && (
        <ul className="jd-closed__list">
          {jobs.map((j, i) => (
            <li key={j.id}>
              <Link
                className="jd-closed__job"
                to={buildJobPath(j)}
                onClick={() => siteEvents.act('closed_similar_click', { rank: i + 1 })}
              >
                <span className="jd-closed__job-title">{j.title}</span>
                <span className="jd-closed__job-meta">
                  {[j.company, j.is_remote ? 'Remote' : j.location, j.salary].filter(Boolean).join(' · ')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Link className="jd-closed__more" to={search} onClick={() => siteEvents.act('closed_similar_click', { rank: 0 })}>
        See every open {role ? `${role} role` : 'role like this'} &rarr;
      </Link>

      {alertJob && <JobAlertSignup job={alertJob} variant="closed" />}
    </section>
  );
}
